import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreatePostRequest,
  DeletePostRequest,
  DeletePostResponse,
  SearchPostsRequest,
  SearchPostsResponse,
  UpdatePostRequest,
  UpdatePostResponse,
  ViewPostHierarchyRequest,
  ViewPostHierarchyResponse,
  ViewPostsRequest,
  ViewPostsResponse
} from './post.types';

import {
  CREATING_POST,
  DELETING_POST,
  RETRIEVING_POST,
  RETRIEVING_POSTS,
  SEARCHING_POSTS,
  UPDATING_POST
} from './post.constants';

import {
  requestCreatePost,
  requestDeletePost,
  requestSearchPosts,
  requestUpdatePost,
  requestViewPostHierarchy,
  requestViewPosts
} from './post.actions';

import {
  getCreatePostRequest,
  getDeletePostRequest,
  getSearchPostsRequest,
  getUpdatePostRequest,
  getViewPostHierarchyRequest,
  getViewPostsRequest
} from './post.endpoints';

import {
  addMemberPosts,
  addPosts,
  replaceMemberPost,
  replacePost,
  setMemberPosts,
  setPostHierarchy,
  setPostSearchResults,
  setPosts
} from '../../slices/programme/tribe/tribe.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreatePostRequest({ payload }: SagaPayload<CreatePostRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_POST));

    const [endpoint, options] = getCreatePostRequest(requestData);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_POST));
  }
}

export function* watchForCreatePostRequest() {
  yield takeLatest(requestCreatePost.type, performCreatePostRequest);
}

export function* performViewPostsRequest({ payload }: SagaPayload<ViewPostsRequest>) {
  const { requestData, onSuccess } = payload;
  const { page, ammend, replace, memberExternalReference } = requestData;

  try {
    if (!ammend && !replace) {
      yield put(addSpinner(RETRIEVING_POSTS));
    }

    const [endpoint, options] = getViewPostsRequest(requestData);

    const { data }: AxiosResponse<ViewPostsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    if (ammend && memberExternalReference) {
      yield put(addMemberPosts(data.posts));
    } else if (ammend && !memberExternalReference) {
      yield put(addPosts(data.posts));
    } else if (replace && memberExternalReference) {
      yield put(replaceMemberPost({ page, posts: data.posts }));
    } else if (replace && !memberExternalReference) {
      yield put(replacePost({ page, posts: data.posts }));
    } else if (memberExternalReference) {
      yield put(setMemberPosts(data.posts));
    } else {
      yield put(setPosts(data.posts));
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (!ammend && !replace) {
      yield put(removeSpinner(RETRIEVING_POSTS));
    }
  }
}

export function* watchForViewPostsRequest() {
  yield takeLatest(requestViewPosts.type, performViewPostsRequest);
}

export function* performUpdatePostRequest({ payload }: SagaPayload<UpdatePostRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_POST));

    const [endpoint, options] = getUpdatePostRequest(requestData);

    const { data }: AxiosResponse<UpdatePostResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_POST));
  }
}

export function* watchForUpdatePostRequest() {
  yield takeLatest(requestUpdatePost.type, performUpdatePostRequest);
}

export function* performDeletePostRequest({ payload }: SagaPayload<DeletePostRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_POST));

    const [endpoint, options] = getDeletePostRequest(requestData);

    const { data }: AxiosResponse<DeletePostResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_POST));
  }
}

export function* watchForDeletePostRequest() {
  yield takeLatest(requestDeletePost.type, performDeletePostRequest);
}

export function* performSearchPostsRequest({ payload }: SagaPayload<SearchPostsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(SEARCHING_POSTS));

    const [endpoint, options] = getSearchPostsRequest(requestData);

    const { data }: AxiosResponse<SearchPostsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setPostSearchResults(data.posts));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(SEARCHING_POSTS));
  }
}

export function* watchForSearchPostsRequest() {
  yield takeLatest(requestSearchPosts.type, performSearchPostsRequest);
}

export function* performViewPostHierarchyRequest({ payload }: SagaPayload<ViewPostHierarchyRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_POST));

    const [endpoint, options] = getViewPostHierarchyRequest(requestData);

    const { data }: AxiosResponse<ViewPostHierarchyResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setPostHierarchy(data.post));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_POST));
  }
}

export function* watchForViewPostHierarchyRequest() {
  yield takeLatest(requestViewPostHierarchy.type, performViewPostHierarchyRequest);
}

export default function* postSaga() {
  yield all([
    watchForCreatePostRequest(),
    watchForViewPostsRequest(),
    watchForUpdatePostRequest(),
    watchForDeletePostRequest(),
    watchForSearchPostsRequest(),
    watchForViewPostHierarchyRequest()
  ]);
}
